<template>
  <div>
    <custom-toolbar
      :total="$store.getters['user/total_member']"
      :title="$t('Members')"
      @refresh="getMembers"
    />

    <v-card class="mb-6 mt-6">
      <v-alert
        v-model="isAlertVisible"
        :color="error ? 'error' : 'success'"
        dismissible
        text
        transition="slide-y-transition"
      >
        {{ reponseMsg }}
      </v-alert>
      <div class="px-4 py-3">
        <v-row
          class="members-filters-row"
          dense
          align="center"
        >
          <v-col
            cols="12"
            sm="6"
            lg="5"
          >
            <v-text-field
              v-model="$store.state.user.field.keyword"
              label="Rechercher"
              outlined
              dense
              placeholder="Nom, email, téléphone..."
              hide-details
              clearable
              class="members-filter-field"
              @input="scheduleMembersRefresh"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="2"
          >
            <v-select
              v-model="$store.state.user.field.license_status"
              :items="filterOptions.license_status"
              item-text="label"
              item-value="value"
              label="Statut du permis"
              outlined
              dense
              hide-details
              clearable
              multiple
              class="members-filter-field"
              @input="scheduleMembersRefresh"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="2"
          >
            <v-select
              v-model="$store.state.user.field.road_published"
              :items="filterOptions.road_published"
              item-text="label"
              item-value="value"
              label="Publication de trajet"
              outlined
              dense
              hide-details
              clearable
              multiple
              class="members-filter-field"
              @input="scheduleMembersRefresh"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="2"
          >
            <v-select
              v-model="$store.state.user.field.trip_booked"
              :items="filterOptions.trip_booked"
              item-text="label"
              item-value="value"
              label="Réservation de trajet"
              outlined
              dense
              hide-details
              clearable
              multiple
              class="members-filter-field"
              @input="scheduleMembersRefresh"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="1"
            class="d-flex justify-end"
          >
            <v-btn
              small
              outlined
              color="primary"
              class="members-export-btn px-2"
              :loading="isExporting"
              :disabled="isExporting"
              @click.stop="isExportDialogOpen = true"
            >
              <v-icon small>
                {{ icons.mdiDownload }}
              </v-icon>
              <span class="ml-1">CSV</span>
            </v-btn>
          </v-col>
        </v-row>

        <v-row
          v-for="(filter, index) in $store.state.user.field.dynamic_filters"
          :key="`dynamic-filter-${index}`"
          class="mt-1"
          dense
          align="center"
        >
          <v-col
            cols="12"
            md="4"
          >
            <v-autocomplete
              v-model="filter.field"
              :items="availableDynamicFields(index)"
              item-text="label"
              item-value="value"
              label="Filtre dynamique"
              outlined
              dense
              hide-details
              clearable
              @change="onDynamicFieldChange(index)"
            ></v-autocomplete>
          </v-col>
          <v-col
            v-if="dynamicFilterType(filter.field) === 'select'"
            cols="12"
            md="7"
          >
            <v-select
              v-model="filter.values"
              :items="dynamicFilterValueItems(filter.field)"
              item-text="label"
              item-value="value"
              label="Valeurs"
              outlined
              dense
              hide-details
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              :disabled="!filter.field"
              @input="scheduleMembersRefresh"
            ></v-select>
          </v-col>
          <v-col
            v-else-if="dynamicFilterType(filter.field) === 'users'"
            cols="12"
            md="7"
          >
            <v-autocomplete
              v-model="filter.values"
              :items="memberUserOptionsByIndex[index] || []"
              :loading="memberUserSearchLoading[index]"
              :filter="filterMemberUserItems"
              item-value="id"
              item-text="name"
              label="Membres"
              outlined
              dense
              hide-details
              clearable
              multiple
              chips
              deletable-chips
              placeholder="Rechercher par nom, email, téléphone..."
              :search-input="memberUserSearchQuery[index]"
              @update:search-input="val => onMemberUserSearchInput(index, val)"
              @change="scheduleMembersRefresh"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="item.email">{{ item.email }}</span>
                    <span v-if="item.phone_number"> — {{ item.phone_number }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col
            v-else-if="dynamicFilterType(filter.field) === 'flag'"
            cols="12"
            md="7"
            class="d-flex align-center"
          >
            <span class="text-caption grey--text text--darken-1">
              Filtre actif — oui appliqué automatiquement
            </span>
          </v-col>
          <v-col
            v-else-if="dynamicFilterType(filter.field) === 'city'"
            cols="12"
            md="7"
          >
            <member-city-filter-input
              :key="`city-filter-${index}-${filter.field}`"
              :value="filter.place_ids || []"
              label="Villes"
              placeholder="Rechercher une ville..."
              @input="val => onCityFilterChange(index, val)"
            ></member-city-filter-input>
          </v-col>
          <v-col
            v-else-if="dynamicFilterType(filter.field) === 'date_range'"
            cols="12"
            md="7"
          >
            <date-range-picker-custom
              :date-from="filter.date_from"
              :date-to="filter.date_to"
              label="Période"
              @change="range => onDateRangeChange(index, range)"
            ></date-range-picker-custom>
          </v-col>
          <v-col
            v-else-if="filter.field"
            cols="12"
            md="7"
          >
            <v-text-field
              label="Valeurs"
              outlined
              dense
              hide-details
              disabled
              value="—"
            ></v-text-field>
          </v-col>
          <v-col
            v-else
            cols="12"
            md="7"
          >
            <v-text-field
              label="Valeurs"
              outlined
              dense
              hide-details
              disabled
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            md="1"
            class="d-flex align-center"
          >
            <v-btn
              icon
              color="error"
              @click="removeDynamicFilter(index)"
            >
              <v-icon>{{ icons.mdiDeleteOutline }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row
          class="mt-1 mb-1"
          dense
        >
          <v-col cols="12">
            <v-btn
              text
              small
              color="primary"
              :disabled="!canAddDynamicFilter"
              @click="addDynamicFilter"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiPlus }}
              </v-icon>
              Ajouter un filtre
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <v-dialog
      v-model="isExportDialogOpen"
      max-width="920"
      scrollable
      eager
    >
      <v-card>
        <v-card-title class="text-h6 pb-2">
          Exporter les membres
        </v-card-title>
        <v-card-text class="export-dialog-body pt-2">
          <p class="text-body-2 mb-4 grey--text">
            Les filtres actifs seront appliqués à l'export.
          </p>

          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <div class="text-subtitle-2 mb-3">
                Format de fichier
              </div>
              <v-radio-group
                v-model="exportFormat"
                hide-details
                class="mt-0"
              >
                <v-radio
                  label="CSV (virgule ,)"
                  value="csv_comma"
                ></v-radio>
                <v-radio
                  label="CSV (point-virgule ;)"
                  value="csv_semicolon"
                ></v-radio>
                <v-radio
                  label="Excel (.xlsx)"
                  value="xlsx"
                ></v-radio>
              </v-radio-group>
            </v-col>

            <v-col
              cols="12"
              md="8"
            >
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="text-subtitle-2">
                  Colonnes à exporter
                </div>
                <div>
                  <v-btn
                    x-small
                    text
                    color="primary"
                    @click="selectAllExportColumns"
                  >
                    Tout sélectionner
                  </v-btn>
                  <v-btn
                    x-small
                    text
                    color="primary"
                    @click="resetDefaultExportColumns"
                  >
                    Par défaut
                  </v-btn>
                </div>
              </div>

              <div
                v-for="(group, groupKey) in exportColumnGroups"
                :key="groupKey"
                class="mb-4"
              >
                <div class="text-caption font-weight-bold primary--text mb-2">
                  {{ group.label }}
                </div>
                <v-row dense>
                  <v-col
                    v-for="column in group.columns"
                    :key="column.key"
                    cols="12"
                    sm="6"
                  >
                    <v-checkbox
                      v-model="exportSelectedColumns"
                      :value="column.key"
                      :label="column.label"
                      hide-details
                      dense
                      class="mt-0 pt-0"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="isExportDialogOpen = false"
          >
            Annuler
          </v-btn>
          <v-btn
            color="primary"
            :loading="isExporting"
            :disabled="isExporting || exportSelectedColumns.length === 0"
            @click="confirmExport"
          >
            Exporter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card>
      <v-data-table
        :loading="isDialogVisible"
        loading-text="Chargement..."
        :headers="headers"
        :items="$store.getters['user/members']"
        :items-per-page="itemsPerPage"
        :page.sync="currentPage"
        dense
        fixed-header
        height="500"
        hide-default-footer
        @page-count="currentPage = $event"
      >
        <!-- name -->
        <template #[`item.name`]="{item}">
          <div class="d-flex align-center">
            <v-badge
              :icon="item.is_verified ? icons.mdiCheckDecagram : icons.mdiHelp"
              bottom
              overlap
              class="h-25"
              offset-x="12"
              offset-y="12"
            >
              <v-avatar
                :color="item.profile_picture_path ? '' : 'primary'"
                :class="item.profile_picture_path ? '' : 'v-avatar-light-bg primary--text'"
                size="36"
              >
                <v-img
                  v-if="item.profile_picture_path"
                  :src="`${item.profile_picture_path}`"
                ></v-img>
                <span v-else-if="item.name">{{ item.name ? item.name.slice(0,2).toUpperCase() : null }}</span>
                <v-img
                  v-else
                  :src="require('@/assets/images/avatars/1.png')"
                />
              </v-avatar>
            </v-badge>
            <div class="d-flex flex-column ms-3">
              <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.name }}</span>
              <small>{{ item.phone_number }} {{ item.email }}</small>
            </div>
          </div>
        </template>

        <template #[`item.license_validated_at`]="{item}">
          <v-chip
            v-show="item.license_validated_at !== null"
            color="info"
            small
          >
            <span class="font-weight-bold">{{ item.license_validated_at !== null ? formattingDate(`${item.license_validated_at}`) : null }}</span>
          </v-chip>
        </template>
        <template #[`item.phone_number`]="{item}">
          {{ item.phone_number !== null ? (item.country_code !== null ? `(${item.country_code})` : '') + `${item.phone_number}` : null }}
        </template>

        <template #[`item.created_at`]="{item}">
          {{ formattingDate(`${item.created_at}`) }}
        </template>
        <template #[`item.dynamic_balance`]="{item}">
          <v-chip
            :color="item.is_locked ? 'error' : 'success'"
            :class="`${item.is_locked ? 'error' : 'success'}--text`"
            class="text-bold"
            small
          >
            {{ formatAmount(item.dynamic_balance) }}
          </v-chip>
        </template>

        <!-- action -->
        <template #[`item.actions`]="{ item }">
          <buttons-group
            :item="item"
            :edit="false"
            :status="false"
            :remove="false"
            :whatsapp="true"
            :call="true"
            @view="openItem(item)"
            @lock="deleteItem(item)"
          />
        </template>
      </v-data-table>

      <v-card-text class="pt-2">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <v-text-field
              :value="$store.state.user.field.paginate"
              label="Lignes par page:"
              type="number"
              min="-1"
              max="15"
              hide-details
              @input="getMembersPerPage"
            ></v-text-field>
          </v-col>

          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="currentPage"
              total-visible="6"
              :length="lastPage"
              @input="getMembers"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <confirm-dialog
      :message="messageDialog"
      :dialog-delete="dialogDelete"
      @clickout="dialogDelete = false"
      @submitted="lockUser(item)"
    />
    <loader-dialog :is-dialog-visible="loaderDialog" />
    <user-lock-form-dialog
      :dialog="dialogLock"
      :user="user"
      @clickout="closeDialogLock"
      @submitted="submitting"
      @error="errorForm"
    />
  </div>
</template>

<script>
import {
  mdiSquareEditOutline, mdiDotsVertical, mdiDeleteOutline, mdiPencilOutline, mdiAccountLock, mdiLockOpen, mdiEyeOutline, mdiRefresh,
  mdiCheckDecagram, mdiHelp, mdiDownload, mdiPlus,
} from '@mdi/js'
import LoaderDialog from '@core/components/dialogs/LoaderDialog'
import ConfirmDialog from '@core/components/dialogs/ConfirmDialog'
import UserLockFormDialog from '@core/components/user/UserLockFormDialog'
import { formatDate } from '@core/date/dateFunctions'
import { intToCurrency, notify } from '@core/dynamicComponents/dynamicComponents'
import CustomToolbar from '@core/components/toolbar/CustomToolbar'
import ButtonsGroup from '@core/components/buttons/ButtonsGroup'
import MemberCityFilterInput from '@core/components/autocomplete/MemberCityFilterInput'
import DateRangePickerCustom from '@core/components/datepicker/DateRangePickerCustom'
import { computed, onMounted, ref, watch } from '@vue/composition-api/dist/vue-composition-api'
import store from '@/store'
import router from '@/router'
import userService from '@/store/services/userService'
import Vue from 'vue'

export default {
  name: 'Members',
  components: {
    ButtonsGroup, CustomToolbar, UserLockFormDialog, ConfirmDialog, LoaderDialog, MemberCityFilterInput, DateRangePickerCustom,
  },

  setup() {
    const isLockedColor = {
      false: 'error',
      true: 'success',
    }

    const itemsPerPage = ref(parseInt(store.state.user.field.paginate, 10))
    const currentPage = ref(null)
    const lastPage = ref(null)
    const dialogDelete = ref(false)
    const isDialogVisible = ref(false)
    const loaderDialog = ref(false)
    const isAlertVisible = ref(false)
    const isExporting = ref(false)
    const error = ref(false)
    const messageDialog = ref(null)
    const reponseMsg = ref(null)
    const user = ref(null)
    const dialogLock = ref(false)
    const isExportDialogOpen = ref(false)
    const exportFormat = ref('csv_comma')
    const exportSelectedColumns = ref([])

    const defaultFilterOptions = {
      road_published: [],
      trip_booked: [],
      license_status: [],
      dynamic_filters: {},
      export_columns: {},
      export_columns_default: [],
    }

    const filterOptions = ref({ ...defaultFilterOptions })
    const memberUserOptionsByIndex = ref({})
    const memberUserSearchLoading = ref({})
    const memberUserSearchQuery = ref({})
    const memberUserSearchTimers = {}
    const FILTER_DEBOUNCE_MS = 3000
    let membersRefreshTimer = null

    const exportColumnGroups = computed(() => filterOptions.value.export_columns || {})

    const allExportColumnKeys = computed(() => Object.values(exportColumnGroups.value)
      .flatMap(group => group.columns || [])
      .map(column => column.key))

    const dynamicFilterDefinitions = computed(() => {
      const definitions = filterOptions.value.dynamic_filters || {}
      return Object.entries(definitions).map(([value, definition]) => ({
        value,
        label: definition.group
          ? `${definition.group} — ${definition.label}`
          : definition.label,
        group: definition.group || null,
        type: definition.type || 'select',
        options: definition.options || [],
      }))
    })

    const dynamicFilterType = field => {
      const definition = dynamicFilterDefinitions.value.find(item => item.value === field)

      return definition ? definition.type : 'select'
    }

    const canAddDynamicFilter = computed(() => {
      const usedFields = store.state.user.field.dynamic_filters
        .map(filter => filter.field)
        .filter(Boolean)

      return dynamicFilterDefinitions.value.some(definition => !usedFields.includes(definition.value))
    })

    const formattingDate = date => formatDate(date)

    const closeDialogLock = () => {
      reponseMsg.value = store.state.user.lock_message
      dialogLock.value = false
      isAlertVisible.value = !!reponseMsg
      loaderDialog.value = false
    }

    const formatAmount = amount => intToCurrency(store.state.user.lang, {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
    }, amount)

    const submitting = () => {
      loaderDialog.value = true
    }

    const errorForm = () => {
      loaderDialog.value = false
    }

    const resetDefaultExportColumns = () => {
      exportSelectedColumns.value = [...(filterOptions.value.export_columns_default || [])]
    }

    watch(isExportDialogOpen, isOpen => {
      if (isOpen && exportSelectedColumns.value.length === 0) {
        resetDefaultExportColumns()
      }
    })

    const selectAllExportColumns = () => {
      exportSelectedColumns.value = [...allExportColumnKeys.value]
    }

    const exportMimeTypes = {
      csv_comma: 'text/csv;charset=utf-8;',
      csv_semicolon: 'text/csv;charset=utf-8;',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }

    const buildExportFilename = format => {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
      const extension = format === 'xlsx' ? 'xlsx' : 'csv'

      return `membres-${timestamp}.${extension}`
    }

    const confirmExport = () => {
      if (exportSelectedColumns.value.length === 0) {
        notify(true, true, 'Sélectionnez au moins une colonne.')
        return
      }

      isExporting.value = true
      store.dispatch('user/exportMembers', {
        field: store.state.user.field,
        format: exportFormat.value,
        exportColumns: exportSelectedColumns.value,
      }).then(response => {
        const blob = new Blob(
          [response.data],
          { type: exportMimeTypes[exportFormat.value] || exportMimeTypes.csv_comma },
        )
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.href = url
        link.download = buildExportFilename(exportFormat.value)
        link.click()
        URL.revokeObjectURL(url)
        isExportDialogOpen.value = false
        notify(true, true, 'Export terminé avec succès.')
      }).catch(async err => {
        let message = 'Erreur lors de l\'export.'
        if (err.response?.data instanceof Blob) {
          try {
            const payload = JSON.parse(await err.response.data.text())
            message = payload.message || message
          } catch (parseError) {
            message = 'Erreur lors de l\'export.'
          }
        } else if (err.response?.data?.message) {
          message = err.response.data.message
        }
        notify(true, true, message)
      }).finally(() => {
        isExporting.value = false
      })
    }

    const availableDynamicFields = index => {
      const currentField = store.state.user.field.dynamic_filters[index]?.field
      const usedFields = store.state.user.field.dynamic_filters
        .map((filter, filterIndex) => (filterIndex === index ? null : filter.field))
        .filter(Boolean)

      return dynamicFilterDefinitions.value.filter(definition => (
        definition.value === currentField || !usedFields.includes(definition.value)
      ))
    }

    const dynamicFilterValueItems = field => {
      if (!field) return []
      const definition = dynamicFilterDefinitions.value.find(item => item.value === field)

      return definition ? definition.options : []
    }

    const addDynamicFilter = () => {
      store.state.user.field.dynamic_filters.push({
        field: null,
        values: [],
        date_from: null,
        date_to: null,
        place_ids: [],
      })
    }

    const filterMemberUserItems = (item, queryText) => {
      if (!queryText) {
        return true
      }

      const query = queryText.toLowerCase()

      return [item.name, item.firstname, item.lastname, item.email, item.phone_number]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(query))
    }

    const mergeSelectedMemberUsers = (index, data) => {
      const filter = store.state.user.field.dynamic_filters[index]
      const selectedIds = filter?.values || []
      const existing = memberUserOptionsByIndex.value[index] || []
      const selectedUsers = existing.filter(user => selectedIds.includes(user.id))
      const merged = [...selectedUsers]

      data.forEach(member => {
        if (!merged.find(item => item.id === member.id)) {
          merged.push(member)
        }
      })

      Vue.set(memberUserOptionsByIndex.value, index, merged)
    }

    const onMemberUserSearchInput = (index, keyword) => {
      Vue.set(memberUserSearchQuery.value, index, keyword)
      searchMembersForFilter(index, keyword)
    }

    const loadMemberUsersForFilter = (index, keyword = null) => {
      const filter = store.state.user.field.dynamic_filters[index]

      if (!filter || filter.field !== 'member_users') {
        return
      }

      const trimmedKeyword = keyword ? String(keyword).trim() : ''

      if (!trimmedKeyword && (!filter.values || filter.values.length === 0)) {
        Vue.set(memberUserOptionsByIndex.value, index, [])
        return
      }

      Vue.set(memberUserSearchLoading.value, index, true)

      userService.searchMembersForFilter(trimmedKeyword, filter.values || []).then(response => {
        mergeSelectedMemberUsers(index, response.data.result?.data || [])
      }).finally(() => {
        Vue.set(memberUserSearchLoading.value, index, false)
      })
    }

    const searchMembersForFilter = (index, keyword) => {
      if (memberUserSearchTimers[index]) {
        clearTimeout(memberUserSearchTimers[index])
      }

      memberUserSearchTimers[index] = setTimeout(() => {
        loadMemberUsersForFilter(index, keyword)
      }, 300)
    }

    const onCityFilterChange = (index, placeIds) => {
      const filter = store.state.user.field.dynamic_filters[index]

      if (!filter) {
        return
      }

      filter.place_ids = placeIds || []
      scheduleMembersRefresh()
    }

    const onDateRangeChange = (index, { dateFrom, dateTo }) => {
      const filter = store.state.user.field.dynamic_filters[index]

      if (!filter) {
        return
      }

      filter.date_from = dateFrom || null
      filter.date_to = dateTo || null
      scheduleMembersRefresh()
    }

    const removeDynamicFilter = index => {
      store.state.user.field.dynamic_filters.splice(index, 1)
      scheduleMembersRefresh()
    }

    const onDynamicFieldChange = index => {
      const filter = store.state.user.field.dynamic_filters[index]
      filter.values = []
      filter.date_from = null
      filter.date_to = null
      filter.place_ids = []

      Vue.set(memberUserOptionsByIndex.value, index, [])
      Vue.set(memberUserSearchQuery.value, index, null)

      if (filter.field === 'member_users' && filter.values?.length) {
        loadMemberUsersForFilter(index)
      }

      scheduleMembersRefresh()
    }

    const lockUser = () => {
      loaderDialog.value = true
      store.dispatch('user/lockUser', { user: user.value, field: null }).then(response => {
        reponseMsg.value = response.message
        loaderDialog.value = false
        dialogDelete.value = false
        error.value = false
        isAlertVisible.value = true
        notify(true, true, response.message)
      }).catch(err => {
        reponseMsg.value = err.response.data.message
        loaderDialog.value = false
        dialogDelete.value = false
        error.value = true
        isAlertVisible.value = true
        notify(true, true, err.response.data.message)
      })
    }

    const deleteItemConfirm = () => {
      closeDelete()
    }

    const deleteItem = item => {
      messageDialog.value = item.is_locked ? `Voulez-vous débloquer l'utilisateur ${item.name}?` : `Voulez-vous bloquer l\'utilisateur ${item.name}?`
      user.value = item
      if (item.is_locked) dialogDelete.value = true
      else dialogLock.value = true
    }

    const openItem = item => {
      router.push({ name: 'showuser', params: { id: item.id } })
    }

    const closeDelete = () => {
      dialogDelete.value = false
    }

    const getMembers = (page = 1) => {
      isDialogVisible.value = true
      store.dispatch('user/getMembersList', { page, field: store.state.user.field }).then(() => {
        currentPage.value = store.getters['user/current_page']
        lastPage.value = store.getters['user/last_page']
        isDialogVisible.value = false
      }).catch(err => {
        console.log(err)
      })
    }

    const getMembersPerPage = perPage => {
      if (perPage !== null) {
        isDialogVisible.value = true
        store.state.user.field.paginate = perPage
        itemsPerPage.value = parseInt(perPage, 10)
        store.dispatch('user/getMembersList', {
          page: currentPage.value,
          field: store.state.user.field,
        }).then(() => {
          currentPage.value = store.getters['user/current_page']
          lastPage.value = store.getters['user/last_page']
          isDialogVisible.value = false
        })
      }
    }

    const fetchMembersByFields = () => {
      isDialogVisible.value = true
      store.dispatch('user/getMembersList', {
        page: 1,
        field: store.state.user.field,
      }).then(() => {
        currentPage.value = store.getters['user/current_page']
        lastPage.value = store.getters['user/last_page']
        isDialogVisible.value = false
      })
    }

    const scheduleMembersRefresh = () => {
      if (membersRefreshTimer) {
        clearTimeout(membersRefreshTimer)
      }

      membersRefreshTimer = setTimeout(() => {
        fetchMembersByFields()
      }, FILTER_DEBOUNCE_MS)
    }

    const getMembersByFields = scheduleMembersRefresh

    const loadFilterOptions = () => {
      store.dispatch('user/getMemberFilterOptions').then(({ result }) => {
        filterOptions.value = {
          ...defaultFilterOptions,
          ...result,
        }
        resetDefaultExportColumns()
      }).catch(err => {
        console.log(err)
      })
    }

    watch(() => store.state.user.refreshMemberCount, () => {
      isDialogVisible.value = true
      store.dispatch('user/refreshMembersList', { page: 1, field: store.state.user.field }).then(() => {
        currentPage.value = store.getters['user/current_page']
        lastPage.value = store.getters['user/last_page']
        isDialogVisible.value = false
      }).catch(err => {
        console.log(err)
      })
    }, { deep: true })

    onMounted(() => {
      loadFilterOptions()
      getMembers(store.getters['user/current_page'] ? store.getters['user/current_page'] : null)
    })

    return {
      filterOptions,
      canAddDynamicFilter,
      scheduleMembersRefresh,
      getMembersByFields,
      getMembersPerPage,
      closeDelete,
      getMembers,
      deleteItem,
      openItem,
      deleteItemConfirm,
      lockUser,
      submitting,
      errorForm,
      currentPage,
      lastPage,
      dialogLock,
      dialogDelete,
      isExportDialogOpen,
      exportFormat,
      exportSelectedColumns,
      exportColumnGroups,
      selectAllExportColumns,
      resetDefaultExportColumns,
      isDialogVisible,
      loaderDialog,
      isAlertVisible,
      isExporting,
      error,
      messageDialog,
      reponseMsg,
      user,
      itemsPerPage,
      formatAmount,
      closeDialogLock,
      formattingDate,
      confirmExport,
      addDynamicFilter,
      removeDynamicFilter,
      onDynamicFieldChange,
      availableDynamicFields,
      dynamicFilterValueItems,
      dynamicFilterType,
      memberUserOptionsByIndex,
      memberUserSearchLoading,
      memberUserSearchQuery,
      filterMemberUserItems,
      onMemberUserSearchInput,
      onCityFilterChange,
      onDateRangeChange,
      headers: [
        { text: 'NOM', value: 'name' },
        { text: 'SOLDE', value: 'dynamic_balance' },
        { text: "DATE D'INSCRIPTION", value: 'created_at' },
        { text: 'PERMIS VALIDE LE', value: 'license_validated_at' },
        { text: 'ACTIONS', value: 'actions' },
      ],
      status: {
        1: 'Current',
        2: 'Professional',
        3: 'Rejected',
        4: 'Resigned',
        5: 'Applied',
      },
      is_locked: {
        1: true,
        0: false,
      },
      isLockedColor,
      icons: {
        mdiSquareEditOutline,
        mdiDotsVertical,
        mdiDeleteOutline,
        mdiPencilOutline,
        mdiAccountLock,
        mdiLockOpen,
        mdiEyeOutline,
        mdiRefresh,
        mdiCheckDecagram,
        mdiHelp,
        mdiDownload,
        mdiPlus,
      },
    }
  },
}
</script>

<style scoped>
@media (min-width: 1264px) {
  .members-filters-row {
    flex-wrap: nowrap;
  }

  .members-filters-row > .col {
    min-width: 0;
  }
}

.members-filter-field >>> .v-select__selections {
  min-height: 32px;
}

.members-export-btn {
  min-width: 0;
}

.export-dialog-body {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
